import { lazy } from 'react';

import { lazyNamed } from 'src/utils/lazyNamed.ts';
import AnalogProducts from './containers/AnalogProducts/index.ts';
import { BroadcastProducts } from './containers/BroadcastProducts/index.ts';
import { CompanySettings } from './containers/CompanySettings/index.ts';
import { ConsultingProducts } from './containers/ConsultingProducts/index.ts';
import {
  AdMediaForm,
  AdMediaHistory,
  AdMediaList,
  AdMediumMapping,
  AdPrice,
  AdTypes,
  Layout as DigitalProductsLayout,
  Placement,
  Products,
} from './containers/DigitalProducts/index.ts';
import { PartnerFilialManagement } from './containers/PartnerFilialManagement/index.tsx';
import PointOfSaleProducts from './containers/PointOfSaleProducts/index.ts';
import { PositionGroups } from './containers/PositionGroups/index.ts';
import {
  AdMedias,
  AdPrices,
  AdTypes as PrintProductsAdTypes,
  Availabilities,
  Calendars,
  Layout as PrintProductsLayout,
  Placements,
  Products as PrintProductsRoot,
  SizeConfigs,
} from './containers/PrintProducts/index.ts';
import { ReferencePerAdmedia } from './containers/ReferencePerAdmedia/index.tsx';

export default {
  AccountTypes: lazy(() => import('./containers/AccountTypes/index.tsx')),
  Helmet: lazy(() => import('./components/Helmet.tsx')),
  CampaignTypes: lazy(() => import('./containers/CampaignTypes/index.tsx')),
  CampaignTypesV2: lazyNamed(() => import('./containers/CampaignTypesV2/index.tsx'), 'CampaignTypes'),
  DataImport: lazy(() => import('./containers/DataImport/index.tsx')),
  Industries: lazyNamed(() => import('./containers/Industries/index.tsx'), 'Industries'),
  Labels: lazy(() => import('./containers/Labels/index.tsx')),
  PriceLists: lazy(() => import('./containers/PriceLists/index.tsx')),
  RoleBasedRedirector: lazy(() => import('./components/RoleBasedRedirector.tsx')),

  DigitalProducts: {
    Placement,
    Layout: DigitalProductsLayout,
    AdMediaList,
    AdMediaForm,
    AdMediaHistory,
    Products,
    AdPrice,
    AdTypes,
    AdMediumMapping,
  },

  BroadcastProducts,

  CompanySettings,

  AdSenseSettings: {
    Credentials: lazy(() => import('./containers/AdSense/Credentials/index.ts')),
    Mappings: lazy(() => import('./containers/AdSense/Mappings/index.ts')),
    OauthCallback: lazy(() => import('./containers/AdSense/Credentials/OauthCallback.tsx')),
    Layout: lazy(() => import('./containers/AdSense/Layout.tsx')),
  },

  XandrProgrammatic: {
    Overview: lazy(() => import('./containers/XandrProgrammatic/containers/Overview/index.tsx')),
    WebsitesMapping: lazy(() => import('./containers/XandrProgrammatic/containers/WebsitesMapping/index.tsx')),
    BrandsMapping: lazy(() => import('./containers/XandrProgrammatic/containers/BrandsMapping/index.tsx')),
    Deals: lazy(() => import('./containers/XandrProgrammatic/containers/Deals/index.tsx')),
    BuyerMapping: lazyNamed(() => import('./containers/XandrProgrammatic/containers/BuyerMapping/index.tsx'), 'BuyerMapping'),
    Layout: lazy(() => import('./containers/XandrProgrammatic/components/Layout/index.tsx')),
    OverviewRedirect: lazy(() => import('./containers/XandrProgrammatic/components/OverviewRedirect.tsx')),
  },

  GoogleProgrammatic: {
    WebsitesMapping: lazy(() => import('./containers/GoogleProgrammatic/containers/WebsitesMapping/index.tsx')),
    BrandsMapping: lazy(() => import('./containers/GoogleProgrammatic/containers/BrandsMapping/index.tsx')),
    BuyerMapping: lazyNamed(() => import('./containers/GoogleProgrammatic/containers/BuyerMapping/index.tsx'), 'BuyerMapping'),
    Layout: lazy(() => import('./containers/GoogleProgrammatic/Layout.tsx')),
  },

  AdomikProgrammatic: {
    Layout: lazyNamed(() => import('./containers/AdomikProgrammatic/components/Layout.tsx'), 'Layout'),
    WebsiteMappings: lazyNamed(() => import('./containers/AdomikProgrammatic/pages/WebsiteMappings/index.tsx'), 'WebsiteMappings'),
    BrandMappings: lazyNamed(() => import('./containers/AdomikProgrammatic/pages/BrandMappings/index.tsx'), 'BrandMappings'),
    BuyerMappings: lazyNamed(() => import('./containers/AdomikProgrammatic/pages/BuyerMappings/index.tsx'), 'BuyerMappings'),
    Deals: lazyNamed(() => import('./containers/AdomikProgrammatic/pages/Deals/index.tsx'), 'Deals'),
    SSPMappings: lazyNamed(() => import('./containers/AdomikProgrammatic/pages/SSPMappings/index.tsx'), 'SSPMappings'),
    Seats: lazyNamed(() => import('./containers/AdomikProgrammatic/pages/Seats/index.tsx'), 'Seats'),
  },

  PassendoProgrammatic: {
    Layout: lazy(() => import('src/scenes/Settings/containers/Passendo/Layout.tsx')),
    WebsitesMapping: lazy(() => import('src/scenes/Settings/containers/Passendo/WebsitesMapping/index.tsx')),
  },

  Brands: {
    List: lazy(() => import('./containers/Brands/containers/Brands/index.tsx')),
    Websites: lazy(() => import('./containers/Brands/containers/Websites/index.tsx')),
    Layout: lazy(() => import('./containers/Brands/components/Layout.tsx')),
  },

  PositionGroups,

  PrintProducts: {
    Layout: PrintProductsLayout,
    AdMedias,
    Placements,
    AdTypes: PrintProductsAdTypes,
    Products: PrintProductsRoot,
    AdPrices,
    Calendars,
    SizeConfigs,
    Availabilities,
  },

  ConsultingProducts,
  PointOfSaleProducts,

  Websites: {
    WebsitesList: lazyNamed(() => import('./containers/Websites/containers/WebsitesList/index.tsx'), 'WebsitesList'),
    WebsiteForm: lazyNamed(() => import('./containers/Websites/containers/WebsiteForm/index.tsx'), 'WebsiteForm'),
    WebsiteHistory: lazyNamed(() => import('./containers/Websites/containers/WebsiteHistory/index.tsx'), 'WebsiteHistory'),
  },

  AnalogProducts,
  PartnerFilialManagement,

  SalesUnits: lazyNamed(() => import('./containers/SalesUnits/index.tsx'), 'SalesUnits'),
  ReferencePerAdmedia,
  SpecialDiscounts: lazyNamed(() => import('./containers/SpecialDiscounts/index.tsx'), 'SpecialDiscounts'),
  Contracts: {
    ContractsList: lazyNamed(() => import('./containers/Contracts/containers/ContractsList/index.tsx'), 'ContractsList'),
    ContractForm: lazyNamed(() => import('./containers/Contracts/containers/ContractForm/index.tsx'), 'ContractForm'),
    ContractHistory: lazyNamed(() => import('./containers/Contracts/containers/ContractHistory/index.tsx'), 'ContractHistory'),
    ContractProgress: lazyNamed(() => import('./containers/Contracts/containers/ContractProgress/index.tsx'), 'ContractProgress'),
  },
};
